import { brandedKey } from './brandedKey.js';

/**
 * Adding FilmSloth to a home screen.
 *
 * The app is already installable — it has a manifest, icons and a service
 * worker — but nothing has ever said so. On Android, Chrome fires
 * `beforeinstallprompt` and then waits: unless the page keeps that event, the
 * offer is gone. That is the whole reason this module exists at module scope
 * rather than inside a component. The event arrives during the first load,
 * often before React has mounted, so the listener has to be installed from
 * `main.tsx` before anything else runs.
 *
 * iOS has no such event and no programmatic install at all. There, the only
 * honest thing is a sentence naming the two taps it takes, and only to
 * somebody who is in Safari and has not already installed the app.
 */

interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISSED_KEY = brandedKey('installDismissed');

let deferred: InstallPromptEvent | null = null;
let watching = false;
const listeners = new Set<() => void>();

function announce(): void {
  for (const listener of listeners) listener();
}

/** Called once from `main.tsx`, before React mounts. */
export function watchForInstallPrompt(): void {
  if (typeof window === 'undefined' || watching) return;
  watching = true;

  window.addEventListener('beforeinstallprompt', (event) => {
    // Without this the browser shows its own bar at a moment of its
    // choosing; keeping the event is what lets the offer sit in the page
    // where it makes sense.
    event.preventDefault();
    deferred = event as InstallPromptEvent;
    announce();
  });

  window.addEventListener('appinstalled', () => {
    deferred = null;
    announce();
  });
}

/** Only for tests: forgets a captured offer and the listeners behind it. */
export function resetInstallPrompt(): void {
  deferred = null;
  listeners.clear();
}

export function canInstall(): boolean {
  return deferred !== null;
}

export function subscribeInstallable(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Shows the browser's install dialog. The event is single-use: whatever the
 * answer, it cannot be shown again, so it is dropped here either way.
 */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  const event = deferred;
  if (!event) return 'unavailable';
  deferred = null;
  announce();
  try {
    await event.prompt();
    const { outcome } = await event.userChoice;
    return outcome;
  } catch {
    return 'unavailable';
  }
}

/** True when the app is already running from a home screen icon. */
export function isStandalone(): boolean {
  try {
    if (window.matchMedia('(display-mode: standalone)').matches) return true;
  } catch {
    // No matchMedia: fall through to the iOS-only flag below.
  }
  // Safari's own, from before `display-mode` worked there.
  return (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

/**
 * Whether this is a browser on iOS, where installing is a manual two-tap
 * affair through the share sheet.
 *
 * Every browser on iOS is Safari underneath and every one of them can add to
 * the home screen, so this matches the platform rather than the brand. iPads
 * report themselves as a Mac from iPadOS 13 on, hence the touch check.
 */
export function isIos(userAgent = navigator.userAgent, maxTouchPoints = navigator.maxTouchPoints) {
  if (/iPhone|iPad|iPod/i.test(userAgent)) return true;
  return /Macintosh/i.test(userAgent) && maxTouchPoints > 1;
}

export function installHintDismissed(): boolean {
  try {
    return window.localStorage.getItem(DISMISSED_KEY) === 'yes';
  } catch {
    return false;
  }
}

export function dismissInstallHint(): void {
  try {
    window.localStorage.setItem(DISMISSED_KEY, 'yes');
  } catch {
    // Then it comes back next time, which is a small enough cost.
  }
}
